import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

interface ThemeToggleProps {
  className?: string;
  variant?: 'icon' | 'badge' | 'full';
  showLabel?: boolean;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = '',
  variant = 'icon',
  showLabel = false,
}) => {
  const { theme, isDark, toggleTheme } = useTheme();

  const nextThemeLabel = isDark ? 'Switch to light mode' : 'Switch to dark mode';
  const currentThemeText = isDark ? 'Dark Mode' : 'Light Mode';

  if (variant === 'full' || showLabel) {
    return (
      <button
        type="button"
        id="theme-toggle-full-btn"
        onClick={toggleTheme}
        aria-label={nextThemeLabel}
        title={nextThemeLabel}
        className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 cursor-pointer ${
          isDark
            ? 'bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white border border-slate-700/80'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900 border border-slate-200'
        } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${className}`}
      >
        <span className="flex items-center gap-2">
          {isDark ? (
            <Moon className="w-4 h-4 text-emerald-400 shrink-0" />
          ) : (
            <Sun className="w-4 h-4 text-amber-500 shrink-0" />
          )}
          <span>Appearance</span>
        </span>
        <span
          className={`text-[11px] font-bold px-2 py-0.5 rounded-md transition-colors ${
            isDark ? 'bg-slate-900 text-emerald-400' : 'bg-white text-slate-800 shadow-xs'
          }`}
        >
          {currentThemeText}
        </span>
      </button>
    );
  }

  if (variant === 'badge') {
    return (
      <button
        type="button"
        id="theme-toggle-badge-btn"
        onClick={toggleTheme}
        aria-label={nextThemeLabel}
        title={nextThemeLabel}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 cursor-pointer ${
          isDark
            ? 'bg-slate-800/80 text-slate-300 hover:text-white hover:bg-slate-700 border border-slate-700'
            : 'bg-slate-100 text-slate-700 hover:text-slate-900 hover:bg-slate-200 border border-slate-200'
        } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${className}`}
      >
        {isDark ? (
          <Sun className="w-3.5 h-3.5 text-amber-400 animate-in spin-in-180 duration-300" />
        ) : (
          <Moon className="w-3.5 h-3.5 text-slate-600 animate-in spin-in-180 duration-300" />
        )}
        <span className="text-[11px] font-semibold">{isDark ? 'Light' : 'Dark'}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      id="theme-toggle-btn"
      onClick={toggleTheme}
      aria-label={nextThemeLabel}
      title={nextThemeLabel}
      className={`relative p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 transition-all duration-200 min-h-[40px] min-w-[40px] flex items-center justify-center cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 shadow-xs ${className}`}
    >
      {isDark ? (
        <Sun className="w-4 h-4 text-amber-400 transition-transform duration-300 hover:rotate-45" />
      ) : (
        <Moon className="w-4 h-4 text-slate-700 transition-transform duration-300 hover:-rotate-12" />
      )}
      <span className="sr-only">{nextThemeLabel}</span>
    </button>
  );
};
